import { ContentURL, getContentUrl } from "../../../Utils/ContentURL";
import "./Notifications.scss";

/** Different types of notices displayed in the UI for various events during the phone to CNP booking and tracking flow. These are persistent notifications displayed until the closes it maually or until the event status changed. */
export enum NoticeType {
    DriverNoLongerAvailable = "DriverNoLongerAvailable",
    FixedPriceNotLockedIn = "FixedPriceNotLockedIn",
    FixedPriceNoLongerAvailable = "FixedPriceNoLongerAvailable",
    FixedFareTripCancelledByUser = "FixedFareTripCancelledByUser",
    MeterFareTripCancelledByUser = "MeterFareTripCancelledByUser",
    FixedFareTripCancelledByDriver = "FixedFareTripCancelledByDriver",
    MeterFareTripCancelledByDriver = "MeterFareTripCancelledByDriver",
}

/** Different types of toasters displayed in the UI for various events during the phone to CNP booking and tracking flow. These are temporary notifications displayed only for a specified time duration (e.g. 8 seconds) and then close automatically. */
export enum ToasterType {
    PaymentMethodSaved = "PaymentMethodSaved",
    SavedCardRemoved = "SavedCardRemoved",
    TripStarted = "TripStarted",
    TripCompleted = "TripCompleted",
    TripCancelled = "TripCancelled",
    NoJob = "NoJob",
}

/** Contents of notices. */
export const WellKnownNotices: Record<NoticeType, { NoticeType: NoticeType; PrimaryText: string | ((dispatchBookingId: number) => string); SecondaryText: string }> = {
    DriverNoLongerAvailable: {
        NoticeType: NoticeType.DriverNoLongerAvailable,
        PrimaryText: "Your driver is no longer available.",
        SecondaryText: "We're searching for a new one."
    },
    FixedPriceNotLockedIn: {
        NoticeType: NoticeType.FixedPriceNotLockedIn,
        PrimaryText: "Your fixed price isn't locked in yet.",
        SecondaryText: "Complete payment to secure the fare before pickup."
    },
    FixedPriceNoLongerAvailable: {
        NoticeType: NoticeType.FixedPriceNoLongerAvailable,
        PrimaryText: "Fixed price is no longer available.",
        SecondaryText: "Please pay the driver at the end of your trip."
    },
    FixedFareTripCancelledByUser: {
        NoticeType: NoticeType.FixedFareTripCancelledByUser,
        PrimaryText: "Cab request cancelled.<br>Payment hold released.",
        SecondaryText: "No charge made. Your bank may take some time to remove the hold. <br>Still need a taxi? Call <a href='tel:132227' class='notice-link'>13 2227</a> or <a href='https://book.13cabs.com.au/' class='notice-link'>book online.</a>"
    },
    MeterFareTripCancelledByUser: {
        NoticeType: NoticeType.MeterFareTripCancelledByUser,
        PrimaryText: "Cab request cancelled.",
        SecondaryText: "<br>Still need a taxi? Call <a href='tel:132227' class='notice-link'>13 2227</a> or <a href='https://book.13cabs.com.au/' class='notice-link'>book online.</a>"
    },
    FixedFareTripCancelledByDriver: {
        NoticeType: NoticeType.FixedFareTripCancelledByDriver,
        PrimaryText: (dispatchBookingId: number) => `The driver couldn't locate the pickup point, so Cab request #${dispatchBookingId} was cancelled.<br>Payment hold released.`,
        SecondaryText: "No charge made. Your bank may take some time to remove the hold. <br>Still need a taxi? Call <a href='tel:132227' class='notice-link'>13 2227</a> or <a href='https://book.13cabs.com.au/' class='notice-link'>book online.</a>"
    },
    MeterFareTripCancelledByDriver: {
        NoticeType: NoticeType.MeterFareTripCancelledByDriver,
        PrimaryText: (dispatchBookingId: number) => `The driver couldn't locate the pickup point, so Cab request #${dispatchBookingId} was cancelled.`,
        SecondaryText: "<br>Still need a taxi? Call <a href='tel:132227' class='notice-link'>13 2227</a> or <a href='https://book.13cabs.com.au/' class='notice-link'>book online.</a>"
    },
}

/** Contents of toasters. */
export const WellKnownToasters = {
    PaymentMethodSaved: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.CreditCard),
        DisplayText: "Payment method saved"
    },
    SavedCardRemoved: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.AlertTriangleWhite),
        DisplayText: "Saved card removed"
    },
    TripStarted: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.Route),
        DisplayText: "Your trip has started."
    },
    TripCompleted: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.Flag),
        DisplayText: "Trip complete. Thanks for riding with 13cabs."
    },
    TripCancelled: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.CircleX),
        DisplayText: "Cab request cancelled."
    },
    NoJob: {
        Icon: getContentUrl(ContentURL.images.PhoneToCnp.CircleX),
        DisplayText: "Cab request cancelled by driver."
    }
}

/** Various error messages to be displayed during the voice to CNP booking flow. */
export const WellknownErrorMessages = {
    GenericMessage: {
        Title: "Something went wrong",
        Message: "We couldn't process your request. Please try again later.",
        ButtonText: "Back",
    },
    CancelBookingError: {
        Title: "An error occured",
        Message: "Sorry, we couldn't cancel your booking. Please try again.",
        ButtonText: "Back",
    },
    PaymentDeclinedByBank: {
        Title: "Payment not approved",
        Message: "Your bank declined the payment hold. No charge was made. Try another card or contact your bank.",
        ButtonText: "Close",
    },
    InsufficientFunds: {
        Title: "Payment not approved",
        Message: "Insufficient funds available. Please use an alternative payment method.",
        ButtonText: "Close",
    },
    PaymentVerificationFailed: {
        Title: "Verification failed",
        Message: "Payment method could not be verified. Please use an alternative payment method.",
        ButtonText: "Close",
    },
    FixedPriceExpired: {
        Title: "Fixed price expired",
        Message: "This locked fare is no longer available. Pay the driver directly in the taxi for the metered fare.",
        ButtonText: "Close",
    },
    RemoveCardFailed: {
        Title: "Couldn't remove card",
        Message: "We couldn't remove your saved card. Please try again.",
        ButtonText: "Close",
    },
}

/** Error message data type. */
export interface ErrorMessageType {
    Title: string;
    Message: string;
    ButtonText: string;
}